import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';

import { FormModal, SubmitProps } from '@/components/form-modal.js';
import { FormSingleSelect } from '@/components/forms/form-single-select.js';
import {
  dryRunImportOrgFromEdlinkDistrictEntry,
  getUnimportedEdlinkOrgEntries,
  importOrgFromEdlinkDistrictEntry,
  invalidateOrgQueries,
} from '@/lib/api-calls.js';
import { edlinkIdSchema, EdlinkOrgChartImportSummary } from '@airreading/sync/edlink';
import * as Sentry from '@sentry/react';

import { exportDryRunToCSV, exportSummaryToCSV } from '@/lib/edlink-csv.js';
import { Alert, AlertDescription } from '@/components/ui/alert.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog.js';
import { EdlinkIdCode } from '@/components/alphanumeric-code.js';
import { Button } from '@/components/ui/button.js';
import { EdlinkSyncResult, EdlinkSyncResultView } from '@/components/edlink/edlink-sync-result-view.js';

const formSchema = z.object({
  districtId: edlinkIdSchema,
});

type ImportFromEdlinkProps = {
  trigger: React.ReactNode;
};

export const ImportFromEdlink = ({ trigger }: ImportFromEdlinkProps) => {
  const { mutateAsync, isPending: isImportPending } = useMutation({
    mutationFn: importOrgFromEdlinkDistrictEntry,
  });

  const { mutateAsync: dryRunMutate, isPending: isDryRunPending } = useMutation({
    mutationFn: dryRunImportOrgFromEdlinkDistrictEntry,
  });

  const { data: viableEdlinkDistrictEntries, isError: isEdlinkEntriesError } = useQuery(
    getUnimportedEdlinkOrgEntries(),
  );
  const [syncResult, setSyncResult] = useState<EdlinkSyncResult | null>(null);
  const [dryRunResult, setDryRunResult] = useState<EdlinkSyncResult | null>(null);
  const [confirmPending, setConfirmPending] = useState<z.infer<typeof formSchema> | null>(null);
  const queryClient = useQueryClient();

  const hasSuccess = syncResult?.type === 'success' || dryRunResult?.type === 'success';

  const getEntry = (districtId: string) =>
    viableEdlinkDistrictEntries?.find((entry) => entry.districtId === districtId);

  const onSubmit = ({ formData }: SubmitProps<z.infer<typeof formSchema>>) => {
    setConfirmPending(formData);
  };

  const onConfirmImport = async () => {
    if (!confirmPending) {
      return;
    }

    const { districtId } = confirmPending;
    const entry = getEntry(districtId);
    setConfirmPending(null);

    if (!entry) {
      setSyncResult({ type: 'error', message: `Selected unknown Edlink district ID: ${districtId}` });
      return;
    }

    try {
      const summary = await mutateAsync(entry);
      setDryRunResult(null);
      setSyncResult({ type: 'success', summary, entityName: entry.districtName, entityId: entry.districtId });
      invalidateOrgQueries(queryClient);
      queryClient.invalidateQueries(getUnimportedEdlinkOrgEntries());
    } catch (e: unknown) {
      Sentry.captureException(e, { tags: { feature: 'edlink-import', districtId: entry.districtId } });
      const { userFacingMessage } = airServerErrorFrom(e) || {};
      const errorReason = userFacingMessage ? `Reason: ${userFacingMessage}` : '';

      setSyncResult({
        type: 'error',
        message: ['Edlink district import failed. Please try again or contact support.', errorReason]
          .filter(Boolean)
          .join(' '),
      });
    }
  };

  const onClose = () => {
    setSyncResult(null);
    setDryRunResult(null);
  };

  const isActionDisabled = (districtId: string, isValid: boolean) =>
    !isValid ||
    !districtId ||
    !viableEdlinkDistrictEntries?.some((e) => e.districtId === districtId) ||
    isDryRunPending ||
    isImportPending;

  const onDownloadSummary = () => {
    const result = syncResult?.type === 'success' ? syncResult : dryRunResult?.type === 'success' ? dryRunResult : null;

    if (!result) {
      return;
    }

    const { summary, entityName, entityId } = result;

    if (summary instanceof EdlinkOrgChartImportSummary) {
      exportSummaryToCSV(summary, `${entityName} - ${entityId} - Import Summary.csv`);
    } else {
      exportDryRunToCSV(summary, `${entityName} - ${entityId} - Dry Run Summary.csv`);
    }
  };

  const onDryRun = (districtId: string) => {
    const entry = getEntry(districtId);

    if (!entry) {
      return;
    }

    setDryRunResult(null);
    setSyncResult(null);

    dryRunMutate(entry)
      .then((summary) =>
        setDryRunResult({ type: 'success', summary, entityName: entry.districtName, entityId: entry.districtId }),
      )
      .catch((e: unknown) => {
        Sentry.captureException(e, { tags: { feature: 'edlink-import-dry-run', districtId: entry.districtId } });
        const { userFacingMessage } = airServerErrorFrom(e) || {};
        const errorReason = userFacingMessage ? `Reason: ${userFacingMessage}` : '';

        setDryRunResult({
          type: 'error',
          message: ['Dry run failed. Please try again.', errorReason].filter(Boolean).join(' '),
        });
      });
  };

  return (
    <>
      <AlertDialog open={!!confirmPending} onOpenChange={(open) => !open && setConfirmPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{dryRunResult ? 'Confirm Import' : 'Skip test and import?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {dryRunResult
                ? 'This action will import the records based on the current dry run result.'
                : 'You are about to import data without running a test. This might introduce incorrect records.'}{' '}
              Please confirm before proceeding.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirmImport}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <FormModal
        modalTitle="Import Edlink district"
        onSubmit={onSubmit}
        onClose={onClose}
        confirmBtnLabel="Import"
        hideCancelButton
        submitButtonProps={(form) => {
          const districtId = form.watch('districtId');
          const { isValid } = form.formState;
          return {
            loading: isImportPending,
            disabled: isActionDisabled(districtId, isValid),
          };
        }}
        formSchema={formSchema}
        trigger={trigger}
        isFullScreen={hasSuccess}
        preventExternalClose
        footerContent={
          hasSuccess || viableEdlinkDistrictEntries?.length
            ? ({ form }) => {
                const districtId = form.watch('districtId');
                const { isValid } = form.formState;
                return (
                  <>
                    {hasSuccess && (
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={isDryRunPending || isImportPending}
                        onClick={onDownloadSummary}
                      >
                        {syncResult?.type === 'success' ? 'Download Summary' : 'Download Dry Run Summary'}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="secondary"
                      loading={isDryRunPending}
                      disabled={isActionDisabled(districtId, isValid)}
                      onClick={() => onDryRun(form.getValues('districtId'))}
                    >
                      Dry Run
                    </Button>
                  </>
                );
              }
            : undefined
        }
      >
        {({ form }) => {
          const { isSubmitting } = form.formState;
          return (
            <div className="grid gap-4 py-4">
              {isEdlinkEntriesError && (
                <Alert variant="destructive">
                  <AlertDescription>Could not connect to Edlink API. Please contact engineering.</AlertDescription>
                </Alert>
              )}
              {viableEdlinkDistrictEntries?.length === 0 && (
                <Alert>
                  <AlertDescription>All Edlink districts have already been imported.</AlertDescription>
                </Alert>
              )}
              {viableEdlinkDistrictEntries ? (
                <FormSingleSelect
                  label="Edlink ID"
                  options={viableEdlinkDistrictEntries.map((entry) => entry.districtId)}
                  renderOption={(districtId) => {
                    const entry = viableEdlinkDistrictEntries.find((entry) => entry.districtId === districtId);
                    return entry ? (
                      <>
                        {entry.districtName} -{' '}
                        <EdlinkIdCode code={entry.integrationId} showCopyButton={false} wrapText={false} />
                      </>
                    ) : (
                      districtId
                    );
                  }}
                  form={form}
                  name="districtId"
                  selectProps={{ disabled: isSubmitting || isDryRunPending }}
                />
              ) : null}
              <EdlinkSyncResultView result={dryRunResult} />
              <EdlinkSyncResultView result={syncResult} />
            </div>
          );
        }}
      </FormModal>
    </>
  );
};
